import React, { useEffect, useState } from 'react';
import { cloneDeep, isEmpty } from 'lodash';

import Box from '@mui/material/Box';
import CourseCard from './CourseCard';
import CourseStatus from '../models/CourseStatus';
import ErrorPanel from '../../shared/components/ErrorPanel';
import Grid from '@mui/material/Unstable_Grid2';
import MultiSelect from './MultiSelect';
import NoSearchResultsFound from '../../shared/components/NoSearchResultsFound';
import Pathway from '../models/Pathway';
import PathwayService from '../services/PathwayService';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import SearchTextField from './SearchTextField';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import VisibilitySensor from 'react-visibility-sensor';

interface CourseListPanelProps {
  pathwaysLoading: boolean;
  pathwaysError: boolean;
  pathways: Pathway[];
}

const CourseListPanel = (props: CourseListPanelProps) => {
  const [searchText, setSearchText] = useState('');
  const [filterBy, setFilterBy] = useState<string[]>([]);
  const [pathwaysLoading, setPathwaysLoading] = useState(props.pathwaysLoading);
  const [pathwaysError, setPathwaysError] = useState(props.pathwaysError);
  const [pathways, setPathways] = useState(props.pathways);

  useEffect(
    () => setPathwaysLoading(props.pathwaysLoading),
    [props.pathwaysLoading]
  );
  useEffect(() => setPathwaysError(props.pathwaysError), [props.pathwaysError]);
  useEffect(() => setPathways(props.pathways), [props.pathways]);

  const statusFilterOptions = [
    {
      label: 'Not Started',
      value: 'NotStarted'
    },
    {
      label: 'Incomplete',
      value: 'Incomplete'
    },
    {
      label: 'Completed',
      value: 'Completed'
    },
    {
      label: 'Expired',
      value: 'Expired'
    }
  ] as { label: string; value: CourseStatus }[];

  const onSearch = (searchText: string) => setSearchText(searchText);

  if (pathwaysError) {
    return (
      <ErrorPanel
        title="Whoops!"
        message="Failed to load courses."
        details={SUPPORT_MESSAGE}
      />
    );
  }

  const allPathways = pathways;
  const handleStatusLoad = (pathkey: string, status: CourseStatus) => {
    if (allPathways) {
      const pathway = allPathways.find((p) => {
        return p.pathkey === pathkey;
      });
      if (pathway) {
        pathway.status = status;
      }
    }
  };

  const filteredPathways = PathwayService.filterByStatus(
    PathwayService.filterPathwaysBy(allPathways, searchText),
    filterBy
  );

  const CourseListSkeleton = () => {
    const skeleton = [];
    for (let i = 0; i < 10; i++) {
      skeleton.push(
        <Grid key={i}>
          <Skeleton
            animation="wave"
            variant="rectangular"
            width={300}
            height={100}
          />
        </Grid>
      );
    }

    return (
      <Grid container spacing={2} justifyContent="space-between">
        {skeleton}
      </Grid>
    );
  };

  const Courses = (props: CourseListPanelProps) => (
    <Grid container spacing={2} justifyContent="space-between">
      {filteredPathways?.map((pathway) => (
        <VisibilitySensor key={pathway.pathkey}>
          {(args: { isVisible: boolean }) => (
            <Grid key={pathway.pathkey}>
              <CourseCard
                pathway={pathway}
                isVisible={args.isVisible}
                onStatusLoad={handleStatusLoad}
              />
            </Grid>
          )}
        </VisibilitySensor>
      ))}
    </Grid>
  );

  return (
    <>
      <Box marginBottom={2}>
        <Grid container justifyContent="space-between">
          <Grid>
            <SearchTextField width="300px" onSearch={onSearch} />
          </Grid>
          <Grid>
            <MultiSelect<CourseStatus>
              items={statusFilterOptions}
              width="300px"
              onChange={(selected) => setFilterBy(selected)}
            />
          </Grid>
        </Grid>
      </Box>
      {pathwaysLoading && isEmpty(pathways) ? (
        <CourseListSkeleton />
      ) : isEmpty(pathways) ? (
        <Grid container justifyContent="center">
          <Grid>
            {searchText ? (
              <NoSearchResultsFound searchCriteria={searchText} />
            ) : (
              <Typography variant="h6">You have no active courses.</Typography>
            )}
          </Grid>
        </Grid>
      ) : (
        <Courses {...props} />
      )}
    </>
  );
};

export default CourseListPanel;
